import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { first, last, snakeCase } from 'lodash';
import displayName from 'component-lib/displayName';

import ExportHeader from './Export/ExportHeader';
import ExportContent from './Export/ExportContent';
import ExportFooter from './Export/ExportFooter';

import ExportAssetsSelected from './Export/ExportDropdownViews/ExportAssetsSelected';
import ExportSelectFormat from './Export/ExportDropdownViews/ExportSelectFormat';
import ExportName from './Export/ExportDropdownViews/ExportName';

import styles from './Dropdowns.scss';

export const EXPORT_VIEWS = {
    ASSETS_SELECTED: 'assetsSelected',
    SELECT_FORMAT: 'selectFormat',
    NAME_EXPORT: 'nameExport',
};

export function stateLogic(WrappedComponent) {
    const VIEW_ORDER = Object.keys(EXPORT_VIEWS).map(view => EXPORT_VIEWS[view]);

    return class extends Component {
        static displayName = displayName('Dropdowns/ExportDropdown/StateLogic');

        static propTypes = {
            assetsSelected: PropTypes.number.isRequired,
            assetLimit: PropTypes.number.isRequired,
            exportNames: PropTypes.array.isRequired,
            nameCharacterLimit: PropTypes.number.isRequired,
            onSubmit: PropTypes.func.isRequired,
            searchName: PropTypes.string.isRequired,
            userEmail: PropTypes.string,
        };

        static defaultProps = {
            userEmail: '',
        };

        constructor(props) {
            super(props);

            const { assetsSelected, assetLimit, searchName } = props;

            const isDisabled = assetsSelected > assetLimit;

            this.state = {
                isDisabled,
                currentView: EXPORT_VIEWS.ASSETS_SELECTED,
                error: {
                    duplicateName: this.isDuplicateName(searchName),
                    overLimit: this.isOverLimit(searchName),
                },
                form: {
                    fileFormat: 'eml',
                    name: searchName,
                    sendNotification: true,
                },
                viewOrder: VIEW_ORDER,
            };
        }

        onChangeName = (name) => {
            this.setState((prevState) => {
                const duplicateName = this.isDuplicateName(name);
                const overLimit = this.isOverLimit(name);
                const error = duplicateName || overLimit;
                const nextState = {
                    form: { ...prevState.form, name },
                    isDisabled: true,
                    error: {
                        duplicateName,
                        overLimit,
                    },
                };

                if (name.length && !error) {
                    return { ...nextState, isDisabled: false };
                }

                return nextState;
            });
        };

        onCheckNotification = (checked) => {
            this.setState({
                form: { ...this.state.form, sendNotification: checked },
            });
        };

        onSelectFileFormat = (fileId) => {
            this.setState({ form: { ...this.state.form, fileFormat: fileId } });
        };

        onSubmit = () => {
            if (!this.isLastView) {
                return void 0;
            }
            return () => this.props.onSubmit({ ...this.state.form, userEmail: this.props.userEmail });
        };

        onBack = () => {
            const back = -1;
            if (!this.isFirstView) {
                this.setState({ currentView: this.move(back), isDisabled: false });
            }
        };

        onNext = () => {
            const forward = 1;
            if (!this.isLastView) {
                this.setState((prevState) => {
                    const nextState = { currentView: this.move(forward) };

                    if (prevState.currentView === EXPORT_VIEWS.SELECT_FORMAT) {
                        return { ...nextState, isDisabled: true };
                    }
                    return nextState;
                });
            }
        };

        get isFirstView() {
            return first(this.state.viewOrder) === this.state.currentView;
        }

        get isLastView() {
            return last(this.state.viewOrder) === this.state.currentView;
        }

        move = (direction) => {
            const { currentView, viewOrder } = this.state;
            const currentViewIndex = viewOrder.indexOf(currentView);
            return viewOrder[currentViewIndex + direction];
        };

        isDuplicateName = name => this.props.exportNames.indexOf(name) > -1;

        isOverLimit = name => name.length > this.props.nameCharacterLimit;

        render() {
            return (
                <WrappedComponent
                    {...this.state}
                    {...this.props}
                    onChangeName={this.onChangeName}
                    onCheckNotification={this.onCheckNotification}
                    onSelectFileFormat={this.onSelectFileFormat}
                    onSubmit={this.onSubmit()}
                    onBack={this.onBack}
                    onNext={this.onNext}
                    isFirstView={this.isFirstView}
                    isLastView={this.isLastView}
                />
            );
        }
    };
}

export function viewLogic(props) {
    const {
        allSelected,
        assetLimit,
        assetsSelected,
        error,
        exportNames,
        form,
        getMessage,
        isFirstView,
        isLastView,
        nameCharacterLimit,
        onBack,
        onChangeName,
        onCheckNotification,
        onNext,
        onSelectFileFormat,
        userEmail,
    } = props;

    const renderContent = (page) => {
        const pages = {
            [EXPORT_VIEWS.ASSETS_SELECTED]: {
                component: ExportAssetsSelected,
                props: { allSelected, assetLimit, assetsSelected },
            },
            [EXPORT_VIEWS.SELECT_FORMAT]: {
                component: ExportSelectFormat,
                props: { onSelect: onSelectFileFormat, selectedType: form.fileFormat },
            },
            [EXPORT_VIEWS.NAME_EXPORT]: {
                component: ExportName,
                props: {
                    error,
                    exportNames,
                    getMessage,
                    nameCharacterLimit,
                    onChangeName,
                    onCheckNotification,
                    userEmail,
                    currentName: form.name,
                    sendNotification: form.sendNotification,
                },
            },
        };

        if (page in pages) {
            return pages[page];
        }
        return null;
    };

    const onBackClick = isFirstView ? void 0 : onBack;
    const onNextClick = isLastView ? void 0 : onNext;

    return {
        onBackClick,
        onNextClick,
        renderContent,
    };
}

export function viewContents({
    assetLimit,
    currentView,
    isDisabled,
    getMessage,
    onBackClick,
    onNextClick,
    onSubmit,
    renderContent,
}) {
    const content = renderContent(currentView);

    return (
        <div className={styles['dropdown-export']} data-dropdown="export">
            <ExportHeader
                text={getMessage(`components.dropdowns.export.views.${snakeCase(currentView)}.header`,)}
            />
            <ExportContent>
                {content ? React.createElement(content.component, content.props) : null}
            </ExportContent>
            <ExportFooter
                assetLimit={assetLimit}
                isDisabled={isDisabled}
                onBack={onBackClick}
                onNext={onNextClick}
                onSubmit={onSubmit}
            />
        </div>
    );
}

viewContents.displayName = displayName('Dropdowns/ExportDropdown/ViewContents');

viewContents.propTypes = {
    assetLimit: PropTypes.number.isRequired,
    currentView: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool.isRequired,
    getMessage: PropTypes.func.isRequired,
    onBackClick: PropTypes.func,
    onNextClick: PropTypes.func,
    onSubmit: PropTypes.func.isRequired,
    renderContent: PropTypes.func.isRequired,
};

viewContents.defaultProps = {
    onBackClick: void 0,
    onNextClick: void 0,
};
